import { ClipboardEvent, KeyboardEvent, useState } from 'react';
import { Input } from '@round/creatorbase-ui';

/** Whitespace and commas both separate one target from the next; neither appears inside one. */
const SEPARATORS = /[\s,]+/;

type Props = {
    label: string;
    placeholder?: string;
    disabled?: boolean;
    /** Fired with one or more identifiers: Enter commits what was typed, a paste commits itself. */
    onAdd: (identifiers: string[]) => void;
};

/**
 * The raw end of both target sections: it turns typed or pasted text into identifiers and hands
 * them on. What an identifier means, and whether it is worth keeping, is the section's business.
 */
export const TargetChipsInput = ({ label, placeholder, disabled, onAdd }: Props) => {
    const [value, setValue] = useState('');

    const add = (raw: string) => {
        const identifiers = splitIdentifiers(raw);
        if (!identifiers.length) {
            return;
        }

        onAdd(identifiers);
        setValue('');
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key !== 'Enter') {
            return;
        }

        // The input sits inside a form, where Enter would otherwise submit it.
        e.preventDefault();
        add(value);
    };

    const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        add(e.clipboardData.getData('text'));
    };

    return (
        <Input
            aria-label={label}
            placeholder={placeholder}
            disabled={disabled}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
        />
    );
};

export const splitIdentifiers = (raw: string) => raw.split(SEPARATORS).filter(Boolean);
